import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../../../../../store/utility';
import { backend_server_address } from '../../../../../backendServer';
import type { FeedItem } from '../../components/cardEvents/feed/feedTypes';
import { seedAttachmentsFromFeedItems } from '../../components/cardEvents/attachments/seedAttachmentsCache';
import type { FeedApiQueryParams, MarkObservationBody, NodeObservation, UnmarkObservationResponse } from '../types';

/** Respuesta raw del endpoint /api/feed/ */
export interface RawFeedResponse {
    items: FeedItem[];
    next_cursor: string | null;
    count: number;
}

/** Item del feed legacy por `feed_url` (wins de analytics) — shape propio del
 * endpoint per-bar, distinto del `FeedItem` de /api/feed/. */
export interface FeedByUrlItem {
    source_id: number;
    asset: string;
    node_path: string;
    occurred_at: string;
    action: string;
    diagnosis: string;
    capital: number;
    downtime: number;
    consid?: string;
    is_confirmed: boolean;
    author?: {
        first_name: string;
        last_name: string;
        position?: string;
    };
}

export interface FeedByUrlResponse {
    items: FeedByUrlItem[];
    /** Cursor to the next page (echo back as `cursor`), or null/absent on the last page. The
     * analytics `feed_url`s are `/api/feed/` urls, so they cursor-paginate just like `getEvents`. */
    next_cursor?: string | null;
}

/**
 * Derive the Excel export URL from a feed URL. The export endpoint mirrors the
 * feed list under `.../feed/export/`, accepting the same filters / custom
 * filters; we just splice `export/` in before the query string.
 */
export const toFeedExportUrl = (feedUrl: string): string => {
    if (feedUrl.includes('/export/')) {
        return feedUrl;
    }
    if (feedUrl.includes('/feed/?')) {
        return feedUrl.replace('/feed/?', '/feed/export/?');
    }
    const [path, query] = feedUrl.split('?');
    const base = path.endsWith('/') ? path : `${path}/`;
    return `${base}export/${query ? `?${query}` : ''}`;
};

export const feedAPI = createApi({
    reducerPath: 'feedAPI',
    baseQuery: axiosBaseQuery({ baseUrl: backend_server_address, arrayParamsSerializer: true }),
    tagTypes: ['Events', 'Observations'] as const,
    endpoints: (builder) => ({
        getEvents: builder.query<RawFeedResponse, FeedApiQueryParams>({
            query: (params) => ({ url: '/api/feed/', method: 'GET', params, withCredentials: true }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.items.map((e) => ({ type: 'Events' as const, id: e.source_id })),
                          { type: 'Events' as const, id: 'LIST' },
                      ]
                    : [{ type: 'Events' as const, id: 'LIST' }],
            // The feed already returns each maintenance event's `attachments` inline. Seed the shared
            // `getAttachments` cache so the cards' count badge / media panel read from cache instead
            // of firing a redundant `GET /api/maintenance_module/<kind>/<id>/` per card on load.
            // Upload/delete still invalidate the `Attachment` tag → the endpoint is hit only when the
            // user actually manages attachments. See `seedAttachmentsFromFeedItems` — shared with the
            // legacy `getFeedByUrl` endpoint so both stay in sync.
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    await seedAttachmentsFromFeedItems(dispatch, data.items);
                } catch {
                    // Feed errors are surfaced via the query's own error state.
                }
            },
        }),

        /** Feed legacy: carga la lista de wins desde el `feed_url` que trae cada
         * barra de analytics (URL absoluta del backend, se usa tal cual). */
        getFeedByUrl: builder.query<FeedByUrlResponse, string>({
            query: (feedUrl) => ({ url: feedUrl, method: 'GET', withCredentials: true }),
            // Same list tag as `getEvents`: a `feed_url` can carry a filter (the panel-action
            // shortcuts list wins pending quantification / confirmation), so a mutation changes
            // which items belong in the list, not just how one renders. The win mutations patch
            // their card in place for an instant update and invalidate this tag to settle
            // membership.
            providesTags: [{ type: 'Events' as const, id: 'LIST' }],
            // Same seeding as `getEvents` — the legacy per-bar feed items carry `type`/`attachments`
            // inline at runtime even though `FeedByUrlItem` predates those fields. Seed the shared
            // `getAttachments` cache so the Victorias drawer's cards don't re-fetch
            // `/<kind>/<id>/` on load.
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    await seedAttachmentsFromFeedItems(
                        dispatch,
                        data.items as unknown as Array<{
                            type?: string;
                            source_id?: number;
                            attachments?: unknown;
                        }>,
                    );
                } catch {
                    // Feed errors are surfaced via the query's own error state.
                }
            },
        }),

        /** Excel export of the same per-bar analytics feed loaded by `getFeedByUrl`.
         * Mirrors the feed URL under `.../feed/export/` (same filters) and streams
         * the backend's binary .xlsx as a Blob. */
        exportFeedByUrl: builder.mutation<Blob, string>({
            query: (feedUrl) => ({
                url: toFeedExportUrl(feedUrl),
                method: 'GET',
                withCredentials: true,
                // axiosBaseQuery uses axios: default responseType 'json' would mangle the
                // binary .xlsx, so ask axios for the raw Blob.
                responseType: 'blob',
            }),
        }),

        // ── Node Observations ─────────────────────────────────────────────────
        getObservations: builder.query<NodeObservation[], { user_id?: number | string } | void>({
            query: (params) => ({
                url: '/api/node_observations/',
                method: 'GET',
                params: params ?? undefined,
                withCredentials: true,
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map((o) => ({ type: 'Observations' as const, id: o.id })),
                          { type: 'Observations' as const, id: 'LIST' },
                      ]
                    : [{ type: 'Observations' as const, id: 'LIST' }],
        }),

        markObservation: builder.mutation<NodeObservation[], MarkObservationBody>({
            query: (body) => ({
                url: '/api/node_observations/mark/',
                method: 'POST',
                data: body,
                withCredentials: true,
            }),
            invalidatesTags: [{ type: 'Observations', id: 'LIST' }],
        }),

        unmarkObservation: builder.mutation<UnmarkObservationResponse, MarkObservationBody>({
            query: (body) => ({
                url: '/api/node_observations/unmark/',
                method: 'POST',
                data: body,
                withCredentials: true,
            }),
            invalidatesTags: [{ type: 'Observations', id: 'LIST' }],
        }),
    }),
});

/** Invalidate the shared Operation-news / Asset-history list so it refetches.
 * Used cross-slice by maintenance/card mutations (RTK Query `invalidatesTags`
 * can't cross `createApi` boundaries). */
export const invalidateFeedEvents = () => feedAPI.util.invalidateTags([{ type: 'Events', id: 'LIST' }]);

export const {
    useGetEventsQuery,
    useGetFeedByUrlQuery,
    useGetObservationsQuery,
    useMarkObservationMutation,
    useUnmarkObservationMutation,
    useExportFeedByUrlMutation,
} = feedAPI;
export const feedAPIReducer = feedAPI.reducer;
export const feedAPIReducerPath = feedAPI.reducerPath;
export const feedAPIReducerMiddleware = feedAPI.middleware;
